<template>
  <div
    class="canvasCard"
    :class="{
      'canvasCard--selected': selected,
      'canvasCard--readonly': readonly,
      'canvasCard--running': running,
    }"
    @click="$emit('select')"
  >
    <!-- Shimmer bar for running state -->
    <div v-if="running" class="canvasCardShimmer" />

    <div class="canvasCardBody">
      <div class="canvasCardTop">
        <div class="canvasCardIcon" :class="iconClass">
          <slot name="icon">
            <AppIcon :name="icon" :size="22" />
          </slot>
        </div>
        <div class="canvasCardHeader">
          <input
            v-if="!readonly && !titleReadonly"
            class="canvasCardTitle"
            type="text"
            v-model="localTitle"
            placeholder="Untitled"
            @click.stop
            @focus="onFocus($event)"
            @blur="onRename($event)"
            @keydown.enter="($event.target as HTMLInputElement).blur()"
          />
          <span v-else class="canvasCardTitleReadonly">{{ title || 'Untitled' }}</span>
          <div class="canvasCardSubtitle"><slot name="subtitle" /></div>
        </div>
        <span v-if="running" class="canvasCardRunningBadge">Running</span>
        <span v-if="hasCountBadge" class="canvasCardCountBadge">
          <slot name="count-badge" />
        </span>
      </div>
      <div v-if="hasDetails" class="canvasCardDetails">
        <slot name="details" />
      </div>
      <span class="canvasCardKind" :class="`canvasCardKind--${iconVariant}`">
        {{ kindLabel }}
        <AppIcon v-if="operatesOn" :name="operatesOn === 'company' ? 'account' : 'contact'" :size="14" />
      </span>
      <div v-if="!readonly" class="canvasCardActions">
        <button
          v-if="canMoveUp"
          class="canvasCardMove"
          type="button"
          title="Move up"
          @click.stop="$emit('moveUp')"
        >
          <AppIcon name="chevron_up" :size="14" />
        </button>
        <button
          v-if="canMoveDown"
          class="canvasCardMove"
          type="button"
          title="Move down"
          @click.stop="$emit('moveDown')"
        >
          <AppIcon name="chevron_down" :size="14" />
        </button>
        <button
          class="canvasCardRemove"
          type="button"
          title="Remove"
          @click.stop="$emit('remove')"
        >
          <AppIcon name="trash" :size="14" />
        </button>
      </div>
    </div>
    <!-- Footer slot for run mode controls -->
    <div v-if="hasFooter" class="canvasCardFooter">
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, useSlots } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'

const props = defineProps<{
  icon: string
  title: string
  selected?: boolean
  iconVariant?: string
  readonly?: boolean
  titleReadonly?: boolean
  running?: boolean
  canMoveUp?: boolean
  canMoveDown?: boolean
  operatesOn?: 'company' | 'person'
}>()

const emit = defineEmits<{
  select: []
  remove: []
  rename: [name: string]
  moveUp: []
  moveDown: []
}>()

const editing = ref(false)
const localTitle = ref(props.title)

// Sync from prop only when not actively editing.
watch(() => props.title, (v) => {
  if (!editing.value) localTitle.value = v
})

function onFocus(e: Event) {
  editing.value = true
  ;(e.target as HTMLInputElement).select()
}

function onRename(e: Event) {
  editing.value = false
  const value = (e.target as HTMLInputElement).value.trim()
  localTitle.value = value || props.title
  if (value && value !== props.title) {
    emit('rename', value)
  }
}

const slots = useSlots()
const hasDetails = Boolean(slots.details)
const hasFooter = Boolean(slots.footer)
const hasCountBadge = Boolean(slots['count-badge'])
const iconClass = `canvasCardIcon--${props.iconVariant || 'default'}`

const kindLabels: Record<string, string> = { source: 'Find', filter: 'Qualify', prospect: 'Prospect', action: 'Action' }
const kindLabel = kindLabels[props.iconVariant || ''] || ''

</script>

<style scoped>
.canvasCard {
  position: relative;
  display: flex;
  flex-direction: column;
  width: fit-content;
  min-width: 100%;
  border: 1px solid var(--color-border-strong, #c0b9ad);
  border-radius: var(--radius-xl, 16px);
  background: var(--color-bg-surface);
  cursor: pointer;
  outline: 2px solid transparent;
  outline-offset: -1px;
  transition: border-color var(--duration-fast) var(--ease-out),
              outline-color var(--duration-fast) var(--ease-out),
              box-shadow var(--duration-fast) var(--ease-out);
}

.canvasCardBody {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-5) var(--space-5) var(--space-7);
}
.canvasCard:hover {
  border-color: var(--color-border-strong, #c0b9ad);
  box-shadow: var(--shadow-neu-out-cream);
}
.canvasCard--selected {
  border-color: var(--color-accent);
  outline-color: var(--color-accent);
}

.canvasCardTop {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.canvasCardIcon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-muted);
  color: var(--color-text-tertiary);
  box-shadow: var(--shadow-neu-out-cream-sm);
}
.canvasCardIcon--source {
  background: var(--color-bg-surface);
  color: var(--color-accent);
}
.canvasCardIcon--filter {
  background: var(--color-bg-surface);
  color: var(--color-text-secondary);
}
.canvasCardIcon--prospect {
  background: var(--color-bg-surface);
  color: var(--color-text-primary);
}
.canvasCardIcon--action {
  background: var(--color-bg-surface);
  color: var(--color-text-secondary);
}

.canvasCardHeader {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.canvasCardTitle {
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  line-height: var(--lh-tight);
  width: 100%;
  padding: 0;
  margin: 0;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: none;
  outline: none;
  cursor: text;
  transition: border-color var(--duration-fast) var(--ease-out);
}
.canvasCardTitle:hover {
  border-color: var(--color-border-subtle);
}
.canvasCardTitle:focus {
  border-color: var(--color-accent);
  background: var(--color-bg-app);
  padding: 0 var(--space-1);
}
.canvasCardSubtitle {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
  line-height: var(--lh-normal);
}

.canvasCardDetails {
  padding-top: var(--space-3);
  border-top: 1px solid var(--color-border-subtle);
}

.canvasCardKind {
  position: absolute;
  bottom: var(--space-2);
  right: var(--space-3);
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-text-tertiary);
  padding: 1px var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
}
.canvasCardKind--source {
  color: #2563eb;
  background: rgba(37, 99, 235, 0.1);
}
.canvasCardKind--filter {
  color: #d97706;
  background: rgba(217, 119, 6, 0.1);
}
.canvasCardKind--prospect {
  color: #059669;
  background: rgba(5, 150, 105, 0.1);
}
.canvasCardKind--action {
  color: #7c3aed;
  background: rgba(124, 58, 237, 0.1);
}

.canvasCardActions {
  position: absolute;
  top: var(--space-2);
  right: var(--space-2);
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: 0;
  transition: opacity var(--duration-fast) var(--ease-out);
}
.canvasCard:hover .canvasCardActions { opacity: 1; }

.canvasCardMove,
.canvasCardRemove {
  background: none;
  border: none;
  color: var(--color-text-tertiary);
  cursor: pointer;
  padding: var(--space-1);
  border-radius: var(--radius-sm);
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color var(--duration-fast) var(--ease-out);
}
.canvasCardMove:hover {
  color: var(--color-text-primary);
}
.canvasCardRemove:hover {
  color: var(--color-danger);
}

/* ── Readonly mode ── */
.canvasCard--readonly {
  cursor: default;
}
.canvasCard--readonly .canvasCardTitle {
  cursor: default;
}

/* ── Running state ── */
.canvasCard--running {
  border-color: var(--color-accent);
}

.canvasCardShimmer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  z-index: 1;
  overflow: hidden;
  background: var(--color-accent-soft, rgba(5, 150, 105, 0.15));
}
.canvasCardShimmer::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, var(--color-accent), transparent);
  animation: shimmer 1.5s ease-in-out infinite;
}
@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

.canvasCardCountBadge {
  flex-shrink: 0;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
}

.canvasCardRunningBadge {
  flex-shrink: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-accent);
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-accent-soft, rgba(5, 150, 105, 0.1));
}

.canvasCardTitleReadonly {
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
  color: var(--color-text-primary);
  line-height: var(--lh-tight);
}

/* ── Footer slot ── */
.canvasCardFooter {
  /* Footer sits below the body, flush to the card edges */
}
</style>
